import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Cpu,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Zap,
  Monitor,
  Smartphone,
  Tv,
  Radio,
  Eye,
  Lock,
} from "lucide-react";

export const Route = createFileRoute("/services_/programmatic-advertising")({
  head: () => ({
    meta: [
      {
        title:
          "Programmatic Advertising & DSP Services — KMWAdvertising · AI Real-Time Bidding",
      },
      {
        name: "description",
        content:
          "Programmatic Advertising & DSP Services That Deliver Smarter Media Buying and Better ROI. Real-Time Bidding (RTB), DV360, CTV, DOOH & Audio by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Programmatic Advertising & DSP Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Reach the Right Audience with AI-Powered Programmatic Advertising. Cross-device DSP media buying, RTB algorithms, and brand safety guarantees.",
      },
    ],
  }),
  component: ProgrammaticAdvertisingServicePage,
});

export function ProgrammaticAdvertisingServicePage() {
  const whyProgrammatic = [
    {
      title: "Smarter Audience Targeting",
      desc: "Reach users based on demographics, interests, browsing behaviour, purchase intent, geo-location, device usage, and first-party customer data.",
      icon: Target,
    },
    {
      title: "Real-Time Optimisation",
      desc: "Algorithmic bidding engines continuously adjust bids, placements, and creative rotators to maximize impression yield and campaign ROI.",
      icon: Cpu,
    },
    {
      title: "Premium Inventory Access",
      desc: "Direct access to thousands of global publisher exchanges, Connected TV networks, OTT platforms, digital audio, and DOOH screens via single DSP consoles.",
      icon: Globe,
    },
    {
      title: "Complete Transparency",
      desc: "Track every ad impression with clear logs on viewability metrics, CTR, CPM, ROAS, brand safety compliance, and invalid traffic (IVT) prevention.",
      icon: ShieldCheck,
    },
    {
      title: "Omnichannel Cross-Device Reach",
      desc: "Deliver unified, frequency-capped messaging across desktop, mobile web, in-app, Smart TVs, streaming audio, and digital billboards.",
      icon: Layers,
    },
  ];

  const dspCapabilities = [
    "Omnichannel campaign strategy & DSP setup",
    "First-party & third-party audience planning",
    "Global & regional ad exchange inventory selection",
    "AI algorithmic bid optimization & RTB rules",
    "Cross-platform budget management & pacing",
    "Deduplicated reach & frequency capping",
    "Dynamic Creative Optimisation (DCO) rotation",
    "Log-level performance monitoring & attribution",
    "Enterprise brand safety & fraud controls",
    "Custom executive BI dashboard reporting",
  ];

  const programmaticChannels = [
    {
      title: "Programmatic Display Advertising",
      desc: "Responsive display banners, HTML5 animated units, dynamic creatives, and interactive retargeting units on premium websites.",
      icon: Monitor,
    },
    {
      title: "Programmatic Video Advertising",
      desc: "In-stream pre-roll/mid-roll, out-stream video, CTV, OTT streaming, rewarded video, and interactive short-form video formats.",
      icon: Tv,
    },
    {
      title: "Digital Audio Advertising",
      desc: "Streaming audio ads on Spotify, podcasts, and digital radio networks with demographic and playlist mood targeting.",
      icon: Radio,
    },
    {
      title: "Digital Out-of-Home (DOOH)",
      desc: "Programmatically bought digital billboards, transit screens, airport displays, and mall screens triggered by location data.",
      icon: Eye,
    },
  ];

  const audienceTargetingMethods = [
    "First-Party Data Onboarding & CRM Matching",
    "Third-Party B2B & B2C Audience Segments",
    "Real-Time Behavioural & Intent Targeting",
    "Contextual Page Content & Sentiment Analysis",
    "Hyper-Local Geographic & Radius Targeting",
    "Device, OS & Connection Speed Targeting",
    "Household & NCCS Demographic Profiling",
    "Interest & Affinity Cluster Segmentation",
    "Lookalike Algorithmic Modelling",
    "Dynamic Multi-Touch Retargeting",
  ];

  const brandSafetyControls = [
    "Premium Publisher Whitelists & Blocklists",
    "Invalid Traffic (IVT) & Bot Detection",
    "Ad Fraud Prevention via IAS & DoubleVerify",
    "MRC-Accredited Viewability Monitoring",
    "Contextual Brand Safety & Sentiment Filters",
    "Harmful Content & Category Exclusions",
    "Domain Verification & Publisher Audits",
    "Ads.txt, App-Ads.txt & Sellers.json Compliance",
  ];

  const performanceKPIs = [
    "Total Impressions & Unique Reach",
    "MRC Viewability Rate (%)",
    "Click-Through Rate (CTR)",
    "Cost Per Mille / Thousand (CPM)",
    "Cost Per Click (CPC)",
    "Cost Per Acquisition (CPA / CPL)",
    "Return on Ad Spend (ROAS)",
    "Video Completion Rate (VCR)",
    "Audio Listen-Through Rate (LTR)",
    "Downstream Conversion Rate",
  ];

  const kmwaStrengths = [
    "25+ years of strategic media planning & buying excellence",
    "Certified DSP specialists across DV360, The Trade Desk, etc.",
    "AI-powered real-time bidding & algorithmic optimization",
    "Enterprise-grade audience targeting & CRM onboarding",
    "100% transparent reporting with zero hidden markup",
    "Strict brand safety, IVT & ad fraud prevention guarantees",
    "True omnichannel media integration across digital & traditional",
    "Creative adaptation for HTML5, DCO & CTV formats",
    "Conversion-focused performance optimization",
    "ROI-driven campaign management framework",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Programmatic Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Cpu className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Programmatic Advertising & DSP Services That Deliver Smarter Media Buying and Better ROI
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                AI-Powered Real-Time Bidding & DSP Management
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Reach the Right Audience with AI-Powered Programmatic Advertising
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Digital advertising has evolved beyond manual media buying. Today's most successful brands rely on Programmatic Advertising to automate media purchases, optimise campaigns in real time and deliver personalised advertising experiences across websites, mobile apps, Connected TV (CTV), OTT platforms, digital audio and digital out-of-home (DOOH).
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Programmatic advertising combines artificial intelligence, machine learning and audience data to ensure every advertising impression is purchased efficiently. Instead of manual site buys, brands use Demand Side Platforms (DSPs) to bid on impressions in real time (RTB), reaching the right audience at the exact moment of influence.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we provide end-to-end Programmatic Advertising and DSP management services designed to improve campaign performance, maximise media efficiency and increase return on advertising investment across the entire digital ecosystem.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Programmatic Media Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY PROGRAMMATIC ADVERTISING */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Automated Precision Media
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Programmatic Advertising?
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Programmatic advertising has transformed digital media buying by enabling advertisers to purchase advertising inventory automatically using real-time data and intelligent bidding algorithms.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyProgrammatic.map((item, idx) => {
                const PIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          ADVANTAGE 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <PIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* OUR PROGRAMMATIC SERVICES & DSP MANAGEMENT */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                DSP & RTB Engine Management
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Programmatic Advertising Services
              </h2>
            </div>

            {/* DSP & RTB GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* DSP */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <Cpu className="h-3.5 w-3.5" />
                    Demand Side Platform Management
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">DSP Media Buying</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Automate Media Buying Across Exchanges with Greater Precision
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  A Demand Side Platform allows purchasing digital inventory across thousands of publishers through a unified bidding console.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {dspCapabilities.map((cap, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{cap}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* RTB & REAL TIME BIDDING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <Zap className="h-3.5 w-3.5" />
                    Millisecond Bidding Algorithms
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Real-Time Bidding (RTB)</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Buy Every Impression Intelligently
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Real-Time Bidding enables bidding on individual ad impressions within milliseconds before a webpage loads, reducing media waste.
                </p>

                <div className="space-y-2 pt-2">
                  <div className="flex items-center gap-2 text-xs text-white/90">
                    <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                    <span>Automated algorithmic impression valuation</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/90">
                    <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                    <span>Lower media waste through bid floor optimization</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/90">
                    <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                    <span>Dynamic impression bid adjustments based on intent</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/90">
                    <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                    <span>Maximizing ROAS & lowering overall eCPM</span>
                  </div>
                </div>
              </div>
            </div>

            {/* PROGRAMMATIC CHANNELS */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  Programmatic Omnichannel Inventory
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Deliver consistent, frequency-capped storytelling across display, video, audio, and digital billboards.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {programmaticChannels.map((chan, idx) => {
                  const CIcon = chan.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <CIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{chan.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{chan.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TARGETING & BRAND SAFETY & KPIS */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Audience Targeting */}
              <div className="p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Target className="h-5 w-5 text-[var(--glow-purple)]" />
                  Audience Targeting
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Intelligent segmentation strategies to reach relevant buyers:
                </p>
                <div className="space-y-1.5 pt-2">
                  {audienceTargetingMethods.map((atm, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span>{atm}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brand Safety */}
              <div className="p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Lock className="h-5 w-5 text-sky-400" />
                  Brand Safety & Fraud
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Enterprise protection ensuring brand safety and zero IVT waste:
                </p>
                <div className="space-y-1.5 pt-2">
                  {brandSafetyControls.map((bsc, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-sky-400 shrink-0" />
                      <span>{bsc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-amber-400" />
                  Analytics & KPIs
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Measured against clearly defined commercial business goals:
                </p>
                <div className="space-y-1.5 pt-2">
                  {performanceKPIs.map((kpi, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-amber-400 shrink-0" />
                      <span>{kpi}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Strategic Human Guidance + AI Speed
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Cpu className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Programmatic DSP Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Scale Your Digital Advertising with Programmatic Media Buying?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our programmatic media specialists today to develop a customised DSP strategy that drives measurable results across display, video, OTT, CTV and digital audio.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Programmatic Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
